import React, { useState } from "react";
import { Avatar } from "antd";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import {
  DashboardOutlined,
  AppstoreOutlined,
  TagsOutlined,
  ShoppingCartOutlined,
  DatabaseOutlined,
  SendOutlined,
  ShopOutlined,
  UserOutlined,
  BarChartOutlined,
  SettingOutlined,
  MedicineBoxOutlined,
  ExperimentOutlined,
  EnvironmentOutlined,
  ScheduleOutlined,
  RightOutlined,
  BellOutlined,
  PictureOutlined,
  PlayCircleOutlined,
  GiftOutlined,
  TrophyOutlined,
  StarOutlined,
  TeamOutlined,
  CrownOutlined,
  ClusterOutlined,
  PercentageOutlined,
  WalletOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";

const VAC_CHILDREN = [
  { key: "vaccine-schedule",    icon: <ScheduleOutlined />,    label: "Schedule" },
  { key: "vaccination-centers", icon: <EnvironmentOutlined />, label: "Centers" },
];

const PLAN_CHILDREN = [
  { key: "vendor-plans",       icon: <StarOutlined />, label: "Vendor Plans" },
  { key: "subscription-plans", icon: <StarOutlined />, label: "Subscription Plans" },
];

const navItems = [
  { key: "dashboard", icon: <DashboardOutlined />,    label: "Dashboard" },
  { key: "products",  icon: <AppstoreOutlined />,     label: "Products" },
  { key: "categories",icon: <TagsOutlined />,         label: "Categories" },
  { key: "hsn-codes", icon: <PercentageOutlined />,    label: "HSN / GST Rates" },
  { key: "coupons",   icon: <GiftOutlined />,         label: "Coupons" },
  { key: "banners",   icon: <PictureOutlined />,      label: "Banners" },
  { key: "orders",    icon: <ShoppingCartOutlined />, label: "Orders" },
  { key: "inventory", icon: <DatabaseOutlined />,     label: "Inventory" },
  { key: "shipping",  icon: <SendOutlined />,         label: "Shipping" },
  { key: "vendor",    icon: <ShopOutlined />,         label: "Vendor" },
  { key: "settlements", icon: <WalletOutlined />,     label: "Settlements" },
  { key: "cod-reconciliation", icon: <SafetyCertificateOutlined />, label: "COD Reconciliation" },
  { key: "sales-team", icon: <TeamOutlined />,        label: "Sales Team" },
  { key: "customers", icon: <UserOutlined />,         label: "Customers" },
  { key: "rewards",   icon: <CrownOutlined />,        label: "XP & Rewards" },
  { key: "analytics", icon: <BarChartOutlined />,     label: "Analytics" },
  { key: "guidance",       icon: <MedicineBoxOutlined />, label: "Guidance" },
  { key: "parenting-videos", icon: <PlayCircleOutlined />, label: "Parenting Videos" },
  { key: "contests",       icon: <TrophyOutlined />,      label: "Baby Contest" },
  { key: "notifications",  icon: <BellOutlined />,       label: "Notifications" },
  { key: "queues",         icon: <ClusterOutlined />,    label: "Message Queues" },
  {
    key: "plans",
    icon: <StarOutlined />,
    label: "Plans",
    children: PLAN_CHILDREN,
  },
  {
    key: "vaccination",
    icon: <ExperimentOutlined />,
    label: "Vaccination",
    children: VAC_CHILDREN,
  },
  { key: "settings",  icon: <SettingOutlined />,      label: "Settings" },
];

// Backend-enforced (@PreAuthorize("hasAuthority('Super Admin')")) — hidden
// here too so a regular Admin never sees a nav item that just 403s for them.
const SUPER_ADMIN_ONLY_KEYS = ["queues"];

export default function Sidebar({ openProfile, loading }) {
  const { user } = useSelector((state) => state.auth);
  const navigate  = useNavigate();
  const location  = useLocation();

  const isSuperAdmin = user?.role?.name === "Super Admin";

  // Right after a refresh, MainLayout's /validate call is still in flight and
  // `user` is momentarily null — computing nav items from that would show the
  // Super Admin-only items for a moment before snapping to the real, restricted
  // one. Show nothing instead of guessing at a role.
  const visibleNavItems = loading
    ? []
    : navItems.filter((n) => isSuperAdmin || !SUPER_ADMIN_ONLY_KEYS.includes(n.key));

  const currentPath = location.pathname.replace("/", "") || "dashboard";
  const groupsWithChildren = navItems.filter((n) => n.children);

  // Any group whose current URL matches one of its children starts open;
  // each group's open/closed state is tracked independently by its own key
  // (not a single shared flag) so opening one never affects the others.
  const [openGroups, setOpenGroups] = useState(() => {
    const initial = {};
    groupsWithChildren.forEach((g) => {
      initial[g.key] = g.children.some((c) => c.key === currentPath);
    });
    return initial;
  });

  const toggleGroup = (key) => setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <div
      style={{
        width: 220,
        background: "linear-gradient(160deg, #2d2255 0%, #1a1540 100%)",
        color: "#fff",
        height: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* LOGO */}
      <div style={{ padding: 16, display: "flex", alignItems: "center", gap: 10 }}>
        <img src="/icon.png" alt="Kidodom" style={{ width: 36, height: 36, borderRadius: 10, objectFit: "contain" }} />
        <span style={{ fontSize: 18, fontWeight: 700, letterSpacing: "-0.3px" }}>Kidodom</span>
      </div>

      {/* NAV ITEMS */}
      <div className="sidebar-scroll" style={{ flex: 1, overflowY: "auto" }}>
        {loading && (
          <div style={{ padding: "12px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
            {[...Array(4)].map((_, i) => (
              <div key={i} style={{ height: 14, borderRadius: 6, background: "rgba(255,255,255,0.08)" }} />
            ))}
          </div>
        )}
        {visibleNavItems.map((n) => {
          if (n.children) {
            // ── Collapsible group ──────────────────────────────────
            const groupKeys   = n.children.map((c) => c.key);
            const groupActive = groupKeys.includes(currentPath);
            const isOpen      = !!openGroups[n.key];
            return (
              <div key={n.key}>
                {/* Parent row */}
                <div
                  onClick={() => toggleGroup(n.key)}
                  style={{
                    padding: "12px 16px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    background: groupActive && !isOpen
                      ? "rgba(255,255,255,0.15)"
                      : "transparent",
                    transition: "0.2s",
                  }}
                >
                  <span>{n.icon}</span>
                  <span style={{ flex: 1 }}>{n.label}</span>
                  <RightOutlined
                    style={{
                      fontSize: 10,
                      opacity: 0.6,
                      transform: isOpen ? "rotate(90deg)" : "rotate(0deg)",
                      transition: "transform 0.2s",
                    }}
                  />
                </div>

                {/* Sub-items */}
                {isOpen && (
                  <div style={{ borderLeft: "2px solid rgba(255,255,255,0.15)", marginLeft: 28 }}>
                    {n.children.map((child) => {
                      const isActive = currentPath === child.key;
                      return (
                        <div
                          key={child.key}
                          onClick={() => navigate(`/${child.key}`)}
                          style={{
                            padding: "10px 14px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            fontSize: 13,
                            background: isActive
                              ? "rgba(255,255,255,0.15)"
                              : "transparent",
                            transition: "0.2s",
                          }}
                        >
                          <span style={{ opacity: 0.8 }}>{child.icon}</span>
                          <span>{child.label}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }

          // ── Regular item ─────────────────────────────────────────
          const isActive = currentPath === n.key;
          return (
            <div
              key={n.key}
              onClick={() => navigate(`/${n.key}`)}
              style={{
                padding: "12px 16px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 10,
                background: isActive ? "rgba(255,255,255,0.15)" : "transparent",
                transition: "0.2s",
              }}
            >
              <span>{n.icon}</span>
              <span>{n.label}</span>
              {n.badge && (
                <span
                  style={{
                    marginLeft: "auto",
                    fontSize: 10,
                    background: "rgba(255,255,255,0.2)",
                    padding: "2px 6px",
                    borderRadius: 20,
                  }}
                >
                  {n.badge}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* PROFILE SECTION */}
      <div
        onClick={openProfile}
        style={{
          padding: 12,
          borderTop: "1px solid rgba(255,255,255,0.1)",
          display: "flex",
          alignItems: "center",
          gap: 10,
          cursor: "pointer",
          background: "rgba(255,255,255,0.05)",
        }}
      >
        <Avatar style={{ background: "#534ab7" }}>
          {user?.avatar || "U"}
        </Avatar>
        <div style={{ overflow: "hidden" }}>
          <div style={{ fontSize: 13, fontWeight: 500 }}>{user?.name}</div>
          <div style={{ fontSize: 11, opacity: 0.6 }}>{user?.role?.name}</div>
        </div>
      </div>
    </div>
  );
}
